/**
 * SEO用のメタタグとOGPタグを生成するヘルパー関数
 */

/**
 * JSON-LD構造化データを `<script type="application/ld+json">` へ
 * `dangerouslySetInnerHTML` で埋め込むための安全な文字列化関数。
 *
 * `JSON.stringify` の結果には `</script>` のようなHTMLタグ終端文字列が
 * エスケープされずに残り得るため、そのままscriptタグに埋め込むと
 * 反射型XSSの原因になる（ユーザー入力由来のラベル・名前・説明文などに
 * `</script><script>...</script>` を含めることでscriptタグを
 * 早期終了させ、任意のHTMLを注入できてしまう）。
 *
 * すべての `<` を `<` にエスケープすることで `</script`
 * を含むあらゆるタグ開始文字列を無害化する。あわせて、JavaScript構文上
 * 問題になり得る U+2028 / U+2029 (行区切り文字) もエスケープする。
 */
const LS = String.fromCharCode(0x2028);
const PS = String.fromCharCode(0x2029);
const LS_PS_PATTERN = new RegExp(`[${LS}${PS}]`, "g");

export function safeJsonLdStringify(data: unknown): string {
  return (JSON.stringify(data) ?? "null")
    .replace(/</g, "\\u003c")
    .replace(LS_PS_PATTERN, (char) => (char === LS ? "\\u2028" : "\\u2029"));
}

export interface SEOMetaOptions {
  title: string;
  description: string;
  url?: string;
  canonicalUrl?: string; // 正規URL（URL正規化用）
  image?: string;
  type?: "website" | "article";
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  siteName?: string; // サイト名（指定しない場合はtitleのみ）
  twitterHandle?: string; // Twitterハンドル
  /**
   * title を渡されたまま使う。ブランドを自分で書いたページ向け。
   *
   * **siteName を落とすことで代用しない。** siteName は og:site_name も
   * 作っているので、消すと別のものまで欠ける。ここで分ける
   */
  rawTitle?: boolean;
  /**
   * 多言語の頁がつなぐ相手の一覧 (hreflang)。公開済みの版どうしだけを渡す。
   * x-default を含めるかどうかも呼び出し側が決める (呼び出し側の表が正本)
   */
  alternates?: readonly { hrefLang: string; href: string }[];
  /** og:locale。渡さなければ "ja_JP" のまま (今までの動きを変えない) */
  ogLocale?: string;
}

/**
 * React Router v7のメタ関数で使用するメタタグを生成
 */
export function generateSEOMeta(options: SEOMetaOptions) {
  const {
    title,
    description,
    url,
    canonicalUrl,
    image,
    type = "website",
    keywords = [],
    author,
    publishedTime,
    modifiedTime,
    siteName,
    twitterHandle,
    rawTitle = false,
    alternates = [],
    ogLocale = "ja_JP",
  } = options;

  /*
  siteName が指定されているときだけ title に足す。

  **判定は「siteName を含むか」なので、短いブランドで終わる title は
  すり抜ける。** トップは `- ゆめさく` で終わっていて `ゆめさくコード` を
  含まないため、`- ゆめさく - ゆめさくコード` になっていた (2026-09-06 まで
  本番でそうなっていた)。ブランドを自分で書いたページは rawTitle を立てる
  */
  const fullTitle =
    !rawTitle && siteName && !title.includes(siteName)
      ? `${title} - ${siteName}`
      : title;

  // 正規URLが指定されていない場合はurlを使用
  const canonical = canonicalUrl || url;

  // React Router の meta は JSON-LD を "script:ld+json" キーで受ける。
  // ここに含めていなかったので、呼ぶ側が構造化データを push するたびに
  // 型エラーになっていた (実行時は正しく出ていたので気づきにくい)。
  // 押し込むのは呼び出し側なので、器の型を先に広げておく
  const metaTags: Array<
    | { title: string }
    | { name: string; content: string }
    | { property: string; content: string }
    | { tagName: string; rel: string; href: string }
    | { tagName: string; rel: string; hrefLang: string; href: string }
    | { "script:ld+json": object }
  > = [
    // 基本メタタグ
    { title: fullTitle },
    { name: "description", content: description },
    ...(keywords.length > 0
      ? [{ name: "keywords", content: keywords.join(", ") }]
      : []),
    ...(author ? [{ name: "author", content: author }] : []),

    // 正規URLタグ（URL正規化で重複コンテンツを防ぐ）
    ...(canonical ? [{ tagName: "link", rel: "canonical", href: canonical }] : []),

    // hreflang (多言語の版どうしをつなぐ)。x-default を含めるかは alternates の中身次第
    ...alternates.map((alt) => ({
      tagName: "link",
      rel: "alternate",
      hrefLang: alt.hrefLang,
      href: alt.href,
    })),

    // OGPタグ
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: description },
    { property: "og:type", content: type },
    ...(canonical ? [{ property: "og:url", content: canonical }] : []),
    ...(image ? [{ property: "og:image", content: image }] : []),
    ...(siteName ? [{ property: "og:site_name", content: siteName }] : []),
    { property: "og:locale", content: ogLocale },

    // Twitterカード
    { name: "twitter:card", content: "summary_large_image" },
    ...(twitterHandle ? [{ name: "twitter:site", content: twitterHandle }] : []),
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: description },
    ...(image ? [{ name: "twitter:image", content: image }] : []),
  ];

  // 記事タイプの場合は追加メタタグ
  if (type === "article") {
    if (publishedTime) {
      metaTags.push({
        property: "article:published_time",
        content: publishedTime,
      });
    }
    if (modifiedTime) {
      metaTags.push({
        property: "article:modified_time",
        content: modifiedTime,
      });
    }
    if (author) {
      metaTags.push({ property: "article:author", content: author });
    }
  }

  return metaTags;
}

/**
 * パンくずリスト用の構造化データを生成
 */
export interface BreadcrumbItem {
  name: string;
  url?: string;
}

export function generateBreadcrumbStructuredData(
  items: BreadcrumbItem[],
  baseUrl?: string
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      ...(item.url && baseUrl && { item: `${baseUrl}${item.url}` }),
      ...(item.url && !baseUrl && { item: item.url }),
    })),
  };
}

/**
 * コース用の構造化データを生成
 */
export interface CourseStructuredDataOptions {
  name: string;
  description: string;
  url: string;
  image?: string;
  provider?: string;
  providerUrl?: string;
  instructor?: string;
  datePublished?: string;
  difficultyLevel?: "Beginner" | "Intermediate" | "Advanced";
}

export function generateCourseStructuredData(
  options: CourseStructuredDataOptions
) {
  const {
    name,
    description,
    url,
    image,
    provider,
    providerUrl,
    instructor,
    datePublished,
    difficultyLevel,
  } = options;

  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name,
    description,
    url,
    ...(image && { image }),
    ...(provider && {
      provider: {
        "@type": "Organization",
        name: provider,
        ...(providerUrl && { url: providerUrl }),
      },
    }),
    ...(instructor && {
      instructor: {
        "@type": "Person",
        name: instructor,
      },
    }),
    ...(datePublished && { datePublished }),
    ...(difficultyLevel && { coursePrerequisites: difficultyLevel }),
  };
}

/**
 * 組織情報の構造化データを生成
 */
export interface OrganizationStructuredDataOptions {
  name: string;
  url: string;
  logo?: string;
  sameAs?: string[];
  contactEmail?: string;
}

export function generateOrganizationStructuredData(
  options: OrganizationStructuredDataOptions
) {
  const { name, url, logo, sameAs, contactEmail } = options;

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name,
    url,
    ...(logo && { logo }),
    ...(sameAs && { sameAs }),
    ...(contactEmail && {
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "Customer Service",
        email: contactEmail,
      },
    }),
  };
}

/**
 * WebSite用の構造化データを生成（検索ボックス付き）
 */
export interface WebSiteStructuredDataOptions {
  name: string;
  url: string;
  searchUrl?: string; // 検索ページのURL（例: https://example.com/search?q={search_term_string}）
}

export function generateWebSiteStructuredData(
  options: WebSiteStructuredDataOptions
) {
  const { name, url, searchUrl } = options;

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name,
    url,
    ...(searchUrl && {
      potentialAction: {
        "@type": "SearchAction",
        target: searchUrl,
        "query-input": "required name=search_term_string",
      },
    }),
  };
}

// Person 構造化データのオプション
export interface PersonStructuredDataOptions {
  name: string;
  slug: string;
  avatarUrl?: string | null;
  jobTitle?: string | null;
  description?: string | null;
  twitterHandle?: string | null;
  githubHandle?: string | null;
  linkedinUrl?: string | null;
  websiteUrl?: string | null;
}

export function generatePersonStructuredData(
  options: PersonStructuredDataOptions
) {
  const {
    name,
    slug,
    avatarUrl,
    jobTitle,
    description,
    twitterHandle,
    githubHandle,
    linkedinUrl,
    websiteUrl,
  } = options;

  const sameAs: string[] = [];
  if (twitterHandle) {
    sameAs.push(`https://twitter.com/${twitterHandle}`);
  }
  if (githubHandle) {
    sameAs.push(`https://github.com/${githubHandle}`);
  }
  if (linkedinUrl) {
    sameAs.push(linkedinUrl);
  }
  if (websiteUrl) {
    sameAs.push(websiteUrl);
  }

  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name,
    url: `https://yumesaku.tech/creators/${slug}`,
    ...(avatarUrl && { image: avatarUrl }),
    ...(jobTitle && { jobTitle }),
    ...(description && { description }),
    ...(sameAs.length > 0 && { sameAs }),
  };
}
